import { readNumber, readNullableString, readOneOf, readRecord, readRecords, readString } from '@/lib/readers';
import type { LiveRow } from '@/lib/pendingWrites';

/** Only records written before imports became synchronous have `queued` or `running`. */
export const IMPORT_STATUSES = ['queued', 'running', 'completed', 'failed'] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

/** Returns true when the import is over. */
export const isImportSettled = (status: ImportStatus) =>
  status === 'completed' || status === 'failed';

/** The row error codes the import names. */
export const IMPORT_ERROR_CODES = [
  'missing_value',
  'invalid_price',
  'invalid_sku',
  'duplicate_product_id',
  'blocked_url',
  'not_an_image',
  'too_large',
  'unreachable',
  'publish_failed',
  'invalid_market',
] as const;

export interface ImportRowError {
  column: string | null;
  code: string;
  detail: string;
}

export interface RejectedRow {
  row: number;
  sku: string;
  errors: ImportRowError[];
}

/** What one import will do. The check returns this before it stores anything. */
export interface ProductImportCheck {
  isFeasible: boolean;
  failureReason: string | null;
  rowCount: number;
  insertCount: number;
  updateCount: number;
  rejectedCount: number;
  rejected: RejectedRow[];
}

export interface ProductImportRecord extends LiveRow {
  id: string;
  fileName: string;
  fileObject: string;
  brand: string;
  status: ImportStatus;
  rowCount: number;
  insertedCount: number;
  updatedCount: number;
  rejectedCount: number;
  rejected: RejectedRow[];
  failureReason: string | null;
  rerunOf: string | null;
  createdBy: string;
  createdAt: string | null;
  updatedAt: string | null;
}

function rejectedRows(value: unknown): RejectedRow[] {
  return readRecords(value).map((row) => ({
    row: readNumber(row.row),
    sku: readString(row.sku),
    errors: readRecords(row.errors).map((error) => ({
      column: readNullableString(error.column),
      code: readString(error.code),
      detail: readString(error.detail),
    })),
  }));
}

/** Returns the result of the import check. */
export function parseProductImportCheck(raw: Record<string, unknown>): ProductImportCheck {
  return {
    isFeasible: raw.is_feasible === true,
    failureReason: readNullableString(raw.failure_reason),
    rowCount: readNumber(raw.row_count),
    insertCount: readNumber(raw.insert_count),
    updateCount: readNumber(raw.update_count),
    rejectedCount: readNumber(raw.rejected_count),
    rejected: rejectedRows(raw.rejected),
  };
}

/** Returns one import record, from Firestore or from an import response, or null when it has no id. */
export function parseProductImport(id: string, raw: Record<string, unknown>): ProductImportRecord | null {
  const importId = readString(raw.import_id) || id;
  if (!importId) return null;
  const metadata = readRecord(raw.metadata);
  return {
    id: importId,
    fileName: readString(raw.file_name),
    fileObject: readString(raw.file_object),
    brand: readString(raw.brand),
    status: readOneOf(IMPORT_STATUSES, raw.status, 'failed'),
    rowCount: readNumber(raw.row_count),
    insertedCount: readNumber(raw.inserted_count),
    updatedCount: readNumber(raw.updated_count),
    rejectedCount: readNumber(raw.rejected_count),
    rejected: rejectedRows(raw.rejected),
    failureReason: readNullableString(raw.failure_reason),
    rerunOf: readNullableString(raw.rerun_of),
    createdBy: readString(metadata.created_by),
    createdAt: readNullableString(metadata.created_at),
    updatedAt: readNullableString(metadata.updated_at),
  };
}
